import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { totpCodeSchema } from "@tc/shared";
import { Button, Icon, LogoIcon, OtpInput, PasswordInput } from "@/components/ui";
import { signIn, twoFactor, useSession } from "@/lib/auth/client";
import { LANG_STORAGE_KEY } from "@/lib/i18n/config";

// Strings come from the shared @tc/i18n catalogues via react-i18next (TW-3434). The login
// screen sits outside <LanguageProvider>, so it renders in the globally-initialized language
// (localStorage → default `es`) — a returning visitor sees their last-used language pre-login.

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * EN/ES switch for the pre-login surface. The login screen sits OUTSIDE <LanguageProvider>,
 * so we can't reuse the shared <LangToggle> (it reads useLanguage(), which throws here).
 * Instead we drive the globally-initialized i18next instance directly and persist the choice
 * to localStorage under the same key config.ts seeds from — so the pick carries into the
 * authenticated app. Spanish is the default for every user (defaultLocale="es"); this is the
 * escape hatch that lets a visitor read the sign-in screen in English.
 */
function AuthLangToggle() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === "en" ? "en" : "es";
  const choose = (next: "en" | "es") => {
    if (next === lang) return;
    void i18n.changeLanguage(next);
    if (typeof window !== "undefined") window.localStorage.setItem(LANG_STORAGE_KEY, next);
    document.documentElement.lang = next;
  };
  return (
    <div className="lang-toggle" role="group" aria-label={t("shell.language")}>
      <button type="button" className={lang === "es" ? "active" : ""} onClick={() => choose("es")}>
        ES
      </button>
      <button type="button" className={lang === "en" ? "active" : ""} onClick={() => choose("en")}>
        EN
      </button>
    </div>
  );
}

interface LocationState {
  from?: { pathname?: string };
}

/** Field-level validation error keys (client-side, before we hit the API). */
type FieldError = "required" | "email" | null;

/** Which step of the sign-in flow is on screen. */
type Step = "credentials" | "totp";

/**
 * Login page (TW-3431, hardened in TW-3455) — the only view reachable without a session.
 * Styled to the "Ink & Gold" design system (docs/design-system.md): a centered auth card on a
 * warm gold-wash stage, brand lockup above, password show/hide, remember-me toggle.
 *
 * Two-factor (TW-3455): when a 2FA-enrolled user submits their password, Better Auth issues NO
 * session — signIn.email resolves with `twoFactorRedirect`. We then show a second step asking
 * for the 6-digit authenticator code (with a "use a backup code" escape hatch); only verifying
 * it mints the session. A passkey shortcut on the first step signs in with WebAuthn directly.
 *
 * On success Better Auth sets the HTTP-only session cookie and we return the user to wherever
 * they were headed (or home).
 */
export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: session, isPending } = useSession();

  const [step, setStep] = useState<Step>("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [fieldError, setFieldError] = useState<FieldError>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Second-factor step state.
  const [code, setCode] = useState("");
  const [useBackup, setUseBackup] = useState(false);
  const [codeError, setCodeError] = useState(false);

  const from = (location.state as LocationState | null)?.from?.pathname ?? "/";

  // Already signed in (e.g. navigated to /login manually) — bounce to the app.
  if (!isPending && session) {
    return <Navigate to={from} replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFieldError(null);
    setAuthError(null);

    // Client-side validation — mirrors the prototype's sign-in checks.
    if (!email.trim() || !password.trim()) {
      setFieldError("required");
      return;
    }
    if (!EMAIL_RE.test(email)) {
      setFieldError("email");
      return;
    }

    setSubmitting(true);
    const result = await signIn.email({ email, password, rememberMe: remember });
    setSubmitting(false);
    if (result.error) {
      setAuthError(result.error.status === 401 ? t("auth.invalidCredentials") : t("auth.genericError"));
      return;
    }
    // 2FA-enrolled user: no session yet — advance to the authenticator challenge.
    if (result.data && "twoFactorRedirect" in result.data && result.data.twoFactorRedirect) {
      setStep("totp");
      return;
    }
    navigate(from, { replace: true });
  }

  async function onVerifyCode(e: FormEvent) {
    e.preventDefault();
    setAuthError(null);
    setCodeError(false);

    // TOTP codes are 6 digits (validated by the shared schema); backup codes are looser, so we
    // only require a non-empty token and let the server reject a bad one.
    if (!useBackup && !totpCodeSchema.safeParse({ code }).success) {
      setCodeError(true);
      return;
    }
    if (useBackup && !code.trim()) {
      setCodeError(true);
      return;
    }

    setSubmitting(true);
    const result = useBackup
      ? await twoFactor.verifyBackupCode({ code: code.trim() })
      : await twoFactor.verifyTotp({ code: code.trim() });
    setSubmitting(false);
    if (result.error) {
      setAuthError(t("auth.mfa.invalidCode"));
      return;
    }
    navigate(from, { replace: true });
  }

  async function onPasskeySignIn() {
    setAuthError(null);
    const result = await signIn.passkey();
    if (result?.error) {
      setAuthError(t("auth.passkey.signInError"));
      return;
    }
    navigate(from, { replace: true });
  }

  const emailInvalid = fieldError === "email";

  return (
    <main className="auth-stage">
      <div className="auth-lang">
        <AuthLangToggle />
      </div>
      <div className="auth-shell">
        <div className="auth-head">
          <div className="auth-wordmark">
            <LogoIcon style={{ height: 38 }} />
            <div style={{ textAlign: "left" }}>
              <div className="wm-name">Travel Connection</div>
              <div className="auth-tagline">{t("auth.tagline")}</div>
            </div>
          </div>
        </div>

        <div className="auth-card">
          {step === "credentials" ? (
            <>
              <h1>{t("auth.title")}</h1>
              <p className="auth-sub">{t("auth.subtitle")}</p>
            </>
          ) : (
            <>
              <h1>{t("auth.mfa.title")}</h1>
              <p className="auth-sub">
                {useBackup ? t("auth.mfa.backupSubtitle") : t("auth.mfa.subtitle")}
              </p>
            </>
          )}

          {authError && (
            <div className="auth-alert" style={{ marginTop: 20 }} role="alert">
              <Icon name="alert" size={17} className="aa-ic" />
              <div className="aa-body">
                <b>{t("auth.errTitle")}</b>
                <br />
                {authError}
              </div>
            </div>
          )}

          {step === "credentials" ? (
            <form className="auth-form" onSubmit={onSubmit} noValidate>
              <div>
                <label className="auth-field-label" htmlFor="login-email">
                  {t("auth.email")}
                </label>
                <div className="input-wrap has-icon">
                  <Icon name="mail" size={16} className="input-icon" />
                  <input
                    id="login-email"
                    className={emailInvalid ? "input input-error" : "input"}
                    type="email"
                    value={email}
                    placeholder={t("auth.emailPlaceholder")}
                    autoComplete="username webauthn"
                    aria-invalid={emailInvalid}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                {emailInvalid && (
                  <div className="field-err">
                    <Icon name="alert" size={13} />
                    {t("auth.errEmail")}
                  </div>
                )}
                {fieldError === "required" && (
                  <div className="field-err">
                    <Icon name="alert" size={13} />
                    {t("auth.errRequired")}
                  </div>
                )}
              </div>

              <div>
                <label className="auth-field-label" htmlFor="login-password">
                  {t("auth.password")}
                </label>
                <PasswordInput
                  id="login-password"
                  icon="lock"
                  value={password}
                  onChange={setPassword}
                  placeholder={t("auth.passwordPlaceholder")}
                  autoComplete="current-password"
                  invalid={Boolean(authError)}
                  showLabel={t("auth.showPassword")}
                  hideLabel={t("auth.hidePassword")}
                />
              </div>

              <div className="auth-row">
                <button type="button" className="remember" onClick={() => setRemember((r) => !r)}>
                  <span className={remember ? "rm-box on" : "rm-box"}>
                    {remember && <Icon name="check" size={12} stroke={2.6} />}
                  </span>
                  {t("auth.remember")}
                </button>
                <button
                  type="button"
                  className="link"
                  onClick={() => navigate("/forgot-password", { state: { email } })}
                >
                  {t("auth.forgot")}
                </button>
              </div>

              <Button type="submit" size="lg" full disabled={submitting}>
                {submitting ? t("auth.signingIn") : t("auth.signIn")}
              </Button>

              <div className="auth-or">{t("auth.passkey.or")}</div>
              <Button type="button" size="lg" full variant="ghost" icon="lock" onClick={onPasskeySignIn}>
                {t("auth.passkey.signIn")}
              </Button>
            </form>
          ) : (
            <form className="auth-form" onSubmit={onVerifyCode} noValidate>
              <div>
                <label className="auth-field-label" htmlFor={useBackup ? "mfa-code" : undefined}>
                  {useBackup ? t("auth.mfa.backupLabel") : t("auth.mfa.codeLabel")}
                </label>
                {useBackup ? (
                  <div className="input-wrap has-icon">
                    <Icon name="lock" size={16} className="input-icon" />
                    <input
                      id="mfa-code"
                      className={codeError ? "input input-error" : "input"}
                      inputMode="text"
                      autoComplete="one-time-code"
                      autoFocus
                      value={code}
                      placeholder={t("auth.mfa.backupPlaceholder")}
                      aria-invalid={codeError}
                      onChange={(e) => {
                        setCode(e.target.value);
                        setCodeError(false);
                      }}
                    />
                  </div>
                ) : (
                  // Segmented one-time-code input — one box per digit, per the design system.
                  <OtpInput
                    value={code}
                    onChange={(v) => {
                      setCode(v);
                      setCodeError(false);
                    }}
                    invalid={codeError}
                    autoFocus
                    ariaLabel={t("auth.mfa.codeLabel")}
                  />
                )}
                {codeError && (
                  <div className="field-err">
                    <Icon name="alert" size={13} />
                    {useBackup ? t("auth.mfa.backupError") : t("auth.mfa.codeError")}
                  </div>
                )}
              </div>

              <Button type="submit" size="lg" full disabled={submitting}>
                {submitting ? t("auth.mfa.verifying") : t("auth.mfa.verify")}
              </Button>

              <button
                type="button"
                className="link"
                style={{ alignSelf: "center" }}
                onClick={() => {
                  setUseBackup((b) => !b);
                  setCode("");
                  setCodeError(false);
                  setAuthError(null);
                }}
              >
                {useBackup ? t("auth.mfa.useApp") : t("auth.mfa.useBackup")}
              </button>
            </form>
          )}
        </div>
      </div>

      <div className="auth-pagefoot">
        <span>© {new Date().getFullYear()} Travel Connection Spain</span>
      </div>
    </main>
  );
}
